import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  Link2,
  Building2,
  TrendingUp,
  ExternalLink,
  Plus,
  Loader2,
  Unlink,
} from "lucide-react";
import { navigateAdmin } from "@/lib/adminNav";
import { extractTmClientNumberFromCostCode } from "@/components/fleet-pulse/helpers";
import CRMAccountSearchDialog, {
  CRMAccountLite,
} from "./CRMAccountSearchDialog";

interface Props {
  ecardId: string;
  taxicallerAccountId: string | null;
  /** Hint passed to the picker as initial query (e.g. customer last name). */
  pickerInitialQuery?: string;
  onChanged?: () => void;
}

const ECardLinkedRecordsPanel: React.FC<Props> = ({
  ecardId,
  taxicallerAccountId,
  pickerInitialQuery,
  onChanged,
}) => {
  const [account, setAccount] = useState<CRMAccountLite | null>(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!taxicallerAccountId) {
      setAccount(null);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("crm_accounts")
      .select(
        "id, account_id, account_name, default_cost_code, traveller_full_name, is_active"
      )
      .eq("account_id", taxicallerAccountId)
      .maybeSingle();
    if (error) {
      console.error("Load linked CRM account failed:", error);
      setAccount(null);
    } else {
      setAccount((data as CRMAccountLite) || null);
    }
    setLoading(false);
  }, [taxicallerAccountId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePickAccount = async (a: CRMAccountLite) => {
    setBusy(true);
    const { error } = await supabase
      .from("ecards")
      .update({ taxicaller_account_id: a.account_id })
      .eq("id", ecardId);
    setBusy(false);
    if (error) {
      toast({
        title: "Link failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Linked to CRM",
      description: `${a.account_id} · ${a.account_name}`,
    });
    onChanged?.();
    await load();
  };

  const handleUnlink = async () => {
    setBusy(true);
    const { error } = await supabase
      .from("ecards")
      .update({ taxicaller_account_id: null })
      .eq("id", ecardId);
    setBusy(false);
    if (error) {
      toast({
        title: "Unlink failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Unlinked from CRM" });
    onChanged?.();
    setAccount(null);
  };

  const tmNumber = extractTmClientNumberFromCostCode(
    account?.default_cost_code
  );

  return (
    <div className="border rounded-md bg-background/50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 uppercase tracking-wide">
          <Link2 className="w-3.5 h-3.5" />
          Linked Records
        </div>
        {account ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px] text-red-600 hover:text-red-700"
            onClick={handleUnlink}
            disabled={busy}
            title="Unlink from CRM"
          >
            {busy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Unlink className="w-3 h-3 mr-1" />
                Unlink
              </>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => setPickerOpen(true)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Link CRM Account
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center text-xs text-muted-foreground py-1.5">
          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
          Loading…
        </div>
      ) : !account ? (
        <div className="text-xs text-muted-foreground italic">
          No CRM account linked.{" "}
          {taxicallerAccountId ? (
            <>
              Stored TC Account ID{" "}
              <span className="font-mono">{taxicallerAccountId}</span> doesn't
              match any CRM record.
            </>
          ) : (
            <>Click "Link CRM Account" to attach this card to a customer.</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {/* CRM */}
          <div className="flex items-center justify-between gap-2 border rounded-md bg-card p-2">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">
                  CRM Account
                </div>
                <div className="text-sm truncate">
                  <span className="font-mono">{account.account_id}</span>
                  <span className="text-muted-foreground"> · </span>
                  {account.account_name}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Badge
                    className={
                      account.is_active ? "bg-green-700" : "bg-gray-600"
                    }
                  >
                    {account.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {account.traveller_full_name && (
                    <span className="text-[10px] text-muted-foreground">
                      {account.traveller_full_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px] flex-shrink-0"
              onClick={() => navigateAdmin("crm", account.account_id)}
              title="Open in CRM Accounts"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              CRM
            </Button>
          </div>

          {/* TM Client # */}
          <div className="flex items-center justify-between gap-2 border rounded-md bg-card p-2">
            <div className="flex items-center gap-2 min-w-0">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">
                  TM Client #
                </div>
                {tmNumber ? (
                  <>
                    <div className="text-sm font-mono">{tmNumber}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {account.default_cost_code}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    No TM cost code
                  </div>
                )}
              </div>
            </div>
            {tmNumber && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px] flex-shrink-0"
                onClick={() => navigateAdmin("fleetpulse", tmNumber)}
                title="Open in Fleet Pulse → TM Client Coverage"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                TM
              </Button>
            )}
          </div>
        </div>
      )}

      <CRMAccountSearchDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialQuery={pickerInitialQuery}
        onPick={handlePickAccount}
      />
    </div>
  );
};

export default ECardLinkedRecordsPanel;
